import React from 'react'
import * as signalR from '@microsoft/signalr'
import { useQueryClient } from '@tanstack/react-query'
import { API_CONFIG } from '../../../api/config'
import { useEditorStore } from '../state/editor-store'
import { useScriptSuggestionsStore } from '../state/script-suggestions-store'

export const useSignalRConnection = (podcastId: string, refetch: () => Promise<void>) => {
  const queryClient = useQueryClient()
  React.useEffect(() => {
    if (!podcastId) return
    
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_CONFIG.BASE_URL}/hubs/podcast-progress`, { withCredentials: true })
      .withAutomaticReconnect()
      .build()

    let mounted = true

    useEditorStore.getState().setScriptJobStatus('idle')
    useEditorStore.getState().clearActivity()

    const markFailed = (msg?: string) => {
      useEditorStore.getState().setScriptJobStatus('failed', msg || 'Disconnected while starting live updates.')
      useEditorStore.getState().setActivity({ phase: 'script', message: 'Failed', percent: 100, step: 'completed' })
    }

    const start = async () => {
      try {
        // Set up event handlers BEFORE starting connection and joining group
        // This ensures handlers are ready when cached progress events arrive
        connection.on('progress', async (evt: Record<string, unknown>) => {
          if (!evt || typeof evt !== 'object') return
          
          if (evt.phase === 'script') {
            handleScriptProgress(evt, refetch)
          } else if (evt.phase === 'script-refine') {
            await handleScriptRefineProgress(evt, refetch)
          } else if (evt.phase === 'synthesis') {
            await handleSynthesisProgress(evt, refetch, queryClient)
          } else if (evt.phase === 'assemble') {
            const message = typeof evt.message === 'string' ? evt.message : 'Assembling episode…'
            const percent = typeof evt.percent === 'number' ? evt.percent : undefined
            const step = typeof evt.step === 'string' ? evt.step : ''
            // Map unknown step strings into our finite set
            const toActivityStep = (s: string): 'starting' | 'in_progress' | 'line_completed' | 'finalizing' | 'completed' => {
              if (s === 'completed' || s === 'all_completed' || s === 'finalized') return 'completed'
              if (s === 'starting' || s === 'queued') return 'starting'
              return 'in_progress'
            }
            useEditorStore.getState().setActivity({ phase: 'assemble', message, percent, step: toActivityStep(step) })
            // If the server signals assembly completion, refetch to pull latest audioFileUrl and clear loader
            if (step === 'completed' || step === 'all_completed' || step === 'finalized') {
              await refetch()
              setTimeout(() => {
                useEditorStore.getState().clearActivity()
              }, 300)
            }
          }
        })

        connection.onclose((err) => {
          if (!mounted) return
          const s = useEditorStore.getState()
          if (s.scriptJobStatus === 'running') {
            markFailed(err?.message)
          }
        })

        await connection.start()
        await connection.invoke('JoinPodcastGroup', podcastId)
        if (!mounted) return
      } catch (e: unknown) {
        const error = e as { message?: string }
        console.warn('SignalR start failed; will rely on auto-reconnect and backend status.', error?.message || e)
      }
    }
    
    void start()

    return () => {
      mounted = false
      const stop = async () => {
        try {
          await connection.invoke('LeavePodcastGroup', podcastId)
        } catch {
          // Ignore errors when leaving group
        }
        try { 
          await connection.stop() 
        } catch {
          // Ignore errors when stopping connection
        }
      }
      void stop()
    }
  }, [podcastId, refetch, queryClient])
}

const handleScriptProgress = async (evt: Record<string, unknown>, refetch: () => Promise<void>) => {
  const step = typeof evt.step === 'string' ? evt.step : ''
  const message = typeof evt.message === 'string' ? evt.message : ''
  const percent = typeof evt.percent === 'number' ? evt.percent : undefined
  const errorMessage = typeof evt.errorMessage === 'string' ? evt.errorMessage : undefined

  // New terminal event emitted by backend after lines are persisted.
  // We simply refetch and clear any loader without displaying its own message.
  if (step === 'script_generation_done') {
    await refetch()
    useEditorStore.getState().setScriptJobStatus('completed')
    useEditorStore.getState().clearActivity()
    return
  }

  if (evt.title || evt.description) {
    useEditorStore.setState((s) => {
      if (!s.podcast) return
      if (typeof evt.title === 'string') s.podcast.title = evt.title
      if (typeof evt.description === 'string') s.podcast.description = evt.description
    })
  }
  
  if (evt.step) {
    const labels: Record<string, string> = {
      generate_title: 'Naming your podcast…',
      scale_content: 'Preparing content…',
      generate_script: 'Writing dialogue…',
      critique_script: 'Reviewing script…',
      revise_script: 'Improving script…',
      track_best_script: 'Selecting best draft…',
      select_final_script: 'Finalizing script…',
      failed: 'Script generation failed',
    }
    useEditorStore.getState().setActivity({ phase: 'script', message: message || labels[step] || 'Working…', percent, step: 'in_progress' })
    useEditorStore.getState().setScriptJobStatus(step === 'failed' ? 'failed' : 'running', errorMessage)
  }
  
  if (step === 'select_final_script') {
    await refetch()
    useEditorStore.getState().setScriptJobStatus('completed')
    useEditorStore.getState().clearActivity()
  }
}

const handleScriptRefineProgress = async (evt: Record<string, unknown>, refetch: () => Promise<void>) => {
  const step = typeof evt.step === 'string' ? evt.step : ''
  const message = typeof evt.message === 'string' ? evt.message : ''
  const percent = typeof evt.percent === 'number' ? evt.percent : undefined
  const summary = typeof evt.summary === 'string' ? evt.summary : undefined
  const diffPreview = typeof evt.diffPreview === 'string' ? evt.diffPreview : undefined
  const linesChanged = typeof evt.linesChanged === 'number' ? evt.linesChanged : undefined
  const errorMessage = typeof evt.errorMessage === 'string' ? evt.errorMessage : undefined

  const updatePodcastSnapshot = () => {
    const fallbackStamp = new Date().toISOString()
    const serverStamp = typeof evt.completedAt === 'string' ? evt.completedAt : fallbackStamp
    useEditorStore.setState((state) => {
      if (!state.podcast) return
      // Persist revision metadata
      if (summary !== undefined) state.podcast.latestScriptRevisionSummary = summary
      if (diffPreview !== undefined) state.podcast.latestScriptRevisionDiffPreview = diffPreview
      if (linesChanged !== undefined) state.podcast.latestScriptRevisionLinesChanged = linesChanged
      if (step === 'applied') {
        state.podcast.latestScriptRevisionAt = serverStamp
      }

      // Flip generating flag based on step
      if (step === 'accepted') {
        state.podcast.isGenerating = true
      }
      if (step === 'applied' || step === 'failed' || step === 'diff_ready') {
        state.podcast.isGenerating = false
      }

      // Ensure loader state is cleared when suggestions are ready
      if (step === 'diff_ready') {
        state.scriptJobStatus = 'completed'
        state.activity = null
      }
    })
  }

  switch (step) {
    case 'accepted': {
      updatePodcastSnapshot()
      useEditorStore.getState().setScriptJobStatus('running')
      useEditorStore.getState().setActivity({
        phase: 'script',
        message: message || 'Applying your updates…',
        percent,
        step: 'starting'
      })
      break
    }
    case 'diff_ready': {
      // Populate suggestions store first to avoid races with any refetch-triggered hydrations
      {
        const pd = Array.isArray((evt as any).proposedDialogue) ? (evt as any).proposedDialogue as Array<{ lineId?: string | null; speaker?: string; line?: string }> : null
        const od = Array.isArray((evt as any).originalDialogue) ? (evt as any).originalDialogue as Array<{ lineId?: string | null; speaker?: string; line?: string }> : null
        const podcast = useEditorStore.getState().podcast
        if (podcast && pd && pd.length > 0) {
          const response = {
            podcastId: podcast.id,
            message: message || 'Proposed script changes are ready',
            summary: summary ?? null,
            diffPreview: diffPreview ?? null,
            linesChanged: typeof linesChanged === 'number' ? linesChanged : null,
            tokenUsage: typeof (evt as any).tokenUsage === 'number' ? (evt as any).tokenUsage : null,
            generatedAt: typeof (evt as any).generatedAt === 'string' ? (evt as any).generatedAt : new Date().toISOString(),
            instruction: typeof (evt as any).instruction === 'string' ? (evt as any).instruction : '',
            originalDialogue: (od ?? []).map((l) => ({
              lineId: l.lineId ?? null,
              speaker: (l.speaker === 'B' ? 'B' : 'A') as 'A' | 'B',
              line: l.line ?? ''
            })),
            proposedDialogue: pd.map((l) => ({
              lineId: l.lineId ?? null,
              speaker: (l.speaker === 'B' ? 'B' : 'A') as 'A' | 'B',
              line: l.line ?? ''
            })),
          }
          useScriptSuggestionsStore.getState().setSuggestions(podcast.id, response as any)
        }
      }
      updatePodcastSnapshot()
      // Suggestions are ready; unlock actions. Do NOT set a new activity to avoid re-showing loader/locking UI.
      // If we want a banner later, surface it in a dedicated, non-blocking component.
      // Signal the editor page to collapse chat when suggestions arrive
      try {
        const p = useEditorStore.getState().podcast
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('podcast:refine-suggestions-ready', { detail: { podcastId: p?.id } }))
        }
      } catch { /* noop */ }
      break
    }
    case 'applied': {
      updatePodcastSnapshot()
      useEditorStore.getState().setScriptJobStatus('completed')
      useEditorStore.getState().setActivity({
        phase: 'script',
        message: summary || message || 'Script updated successfully',
        percent: 100,
        step: 'completed'
      })
      // Keep suggestions visible for review even after apply; user can clear manually
      await refetch()
      // Also signal chat collapse on apply events
      try {
        const p = useEditorStore.getState().podcast
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('podcast:refine-suggestions-ready', { detail: { podcastId: p?.id } }))
        }
      } catch { /* noop */ }
      setTimeout(() => {
        useEditorStore.getState().clearActivity()
      }, 600)
      break
    }
    case 'failed': {
      updatePodcastSnapshot()
      useEditorStore.getState().setScriptJobStatus('failed', errorMessage || message || 'Script refinement failed')
      useEditorStore.getState().setActivity({
        phase: 'script',
        message: errorMessage || message || 'Script refinement failed',
        percent: 100,
        step: 'completed'
      })
      setTimeout(() => {
        useEditorStore.getState().clearActivity()
      }, 2000)
      break
    }
    default: {
      if (message) {
        useEditorStore.getState().setActivity({
          phase: 'script',
          message,
          percent,
          step: 'in_progress'
        })
      }
      break
    }
  }
}

interface SynthesisEstimates {
  estimatedCreditsToSynthesize?: number
}

const extractSynthesisEstimates = (evt: Record<string, unknown>): SynthesisEstimates => {
  const estimates: SynthesisEstimates = {}
  if (typeof evt.estimatedCreditsToSynthesize === 'number') estimates.estimatedCreditsToSynthesize = evt.estimatedCreditsToSynthesize
  return estimates
}

const handleSynthesisProgress = async (
  evt: Record<string, unknown>, 
  refetch: () => Promise<void>, 
  queryClient: ReturnType<typeof useQueryClient>
) => {
  const step = typeof evt.step === 'string' ? evt.step : ''
  const message = typeof evt.message === 'string' ? evt.message : ''
  const percent = typeof evt.percent === 'number' ? evt.percent : undefined

  const { estimatedCreditsToSynthesize: estCreds } = extractSynthesisEstimates(evt)
  if (typeof estCreds === 'number') {
    useEditorStore.setState((s) => {
      if (!s.podcast) return
      if (typeof estCreds === 'number') s.podcast.estimatedCreditsToSynthesize = estCreds
    })
  }

  const stepLabel: Record<string, string> = {
    starting: 'Preparing audio generation…',
    start_line: 'Starting line…',
    tts: 'Synthesizing voice…',
    write: 'Saving audio…',
    line_completed: 'Line completed',
    all_completed: 'All lines rendered',
    failed: 'Audio rendering failed'
  }

  const meta = {
    total: typeof evt.totalLines === 'number' ? evt.totalLines : undefined,
    completed: typeof evt.completedLines === 'number' ? evt.completedLines : undefined,
    index: typeof evt.index === 'number' ? evt.index : undefined,
    lineId: evt.lineId as string | undefined,
    host: evt.host as string | undefined,
  }

  let finalMessage = message || stepLabel[step] || 'Generating audio…'
  if (meta.total && (step === 'start_line' || step === 'tts' || step === 'write' || step === 'line_completed' || step === 'progress')) {
    const done = meta.completed ?? 0
    finalMessage = `Rendering audio… ${done} of ${meta.total} lines complete`
  } else if (step === 'starting' && meta.total) {
    finalMessage = `Rendering ${meta.total} lines…`
  } else if (step === 'all_completed') {
    finalMessage = 'Finished rendering all lines'
  }

  const toActivityStep = (s: string): 'starting' | 'in_progress' | 'line_completed' | 'finalizing' | 'completed' => {
    switch (s) {
      case 'starting': return 'starting'
      case 'line_completed': return 'line_completed'
      case 'all_completed': return 'completed'
      // treat intermediate stages as in_progress
      case 'start_line':
      case 'tts':
      case 'write':
      default:
        return 'in_progress'
    }
  }
  useEditorStore.getState().setActivity({ phase: 'synthesis', message: finalMessage, percent, meta, step: toActivityStep(step) })

  if (step === 'start_line' || step === 'line_completed' || step === 'all_completed' || step === 'failed') {
    await refetch()
    
    if (step === 'line_completed' || step === 'all_completed') {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    }
    
    if (step === 'all_completed') {
      setTimeout(() => {
        useEditorStore.getState().clearActivity()
      }, 500)
    } else if (step === 'failed') {
      setTimeout(() => {
        useEditorStore.getState().clearActivity()
      }, 2000)
    }
  }
}
